import axios, {
  type AxiosInstance,
  type AxiosResponse,
  HttpStatusCode,
  type InternalAxiosRequestConfig,
} from "axios";
import queryString from "query-string";
import { getRuntimeConfig, type RuntimeConfig } from "~/config/runtime.config";
import { ErrorCode } from "~/core/models/ErrorCode.enum";
import { ErrorMessage } from "~/core/models/ErrorMessage.enum";
import LxcError from "~/core/models/LxcError.class";

export default class RestService {
  protected $api: AxiosInstance;
  protected $apiV2: AxiosInstance;
  protected $apiV3: AxiosInstance;
  protected $apiV4: AxiosInstance;
  protected serverUrl: string | undefined;

  constructor() {
    const runtimeConfig: RuntimeConfig = getRuntimeConfig();

    this.serverUrl = runtimeConfig?.LXC_AS_DEVICE_URL;
    const axiosConfig = {
      timeout: 30000,
      withCredentials: true,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    };

    this.$api = axios.create({
      ...axiosConfig,
      baseURL: `${this.serverUrl}/api/v1`,
    });

    this.$apiV2 = axios.create({
      ...axiosConfig,
      baseURL: `${this.serverUrl}/api/v2`,
    });

    this.$apiV3 = axios.create({
      ...axiosConfig,
      baseURL: `${this.serverUrl}/api/v3`,
    });

    this.$apiV4 = axios.create({
      ...axiosConfig,
      baseURL: `${this.serverUrl}/api/v4`,
    });

    this.$api.interceptors.request.use(this.serializeParameters);
    this.$apiV2.interceptors.request.use(this.serializeParameters);
    this.$apiV3.interceptors.request.use(this.serializeParameters);
    this.$apiV4.interceptors.request.use(this.serializeParameters);

    this.$api.interceptors.response.use(
      RestService.onFullFilled,
      RestService.onRejected,
    );
    this.$apiV2.interceptors.response.use(
      RestService.onFullFilled,
      RestService.onRejected,
    );
    this.$apiV3.interceptors.response.use(
      RestService.onFullFilled,
      RestService.onRejected,
    );
    this.$apiV4.interceptors.response.use(
      RestService.onFullFilled,
      RestService.onRejected,
    );
  }

  private serializeParameters(config: InternalAxiosRequestConfig<any>) {
    // Serialize the parameters with library "query-string"
    // It enables to encode brackets in parameters value in order to be compliant with RFC3986, otherwise the backend (AS Spring Boot) will reject the request
    config.paramsSerializer = (params: any) => {
      return (
        queryString
          .stringify(params, { arrayFormat: "bracket" })
          // Also encode the brackets in parameters name
          .replace(/\[/g, "%5B")
          .replace(/]/g, "%5D")
      );
    };
    return config;
  }

  /**
   * Return data from response on success
   * @private
   * @param response
   */
  private static onFullFilled(response: AxiosResponse) {
    if (response.status === HttpStatusCode.NoContent) {
      RestService.logBackstopJsReady(response);

      return null;
    }

    RestService.logBackstopJsReady(response);

    return response.data;
  }

  /**
   * Logout if 401 is received
   * Prepare Error otherwise
   * @param error
   * @private
   */
  private static async onRejected(error: any): Promise<LxcError> {
    const status = error.response?.status;
    if (status === HttpStatusCode.Unauthorized) {
      const { logout } = await import("~/utils/auth.utils");
      logout();
    }

    const lxcError = error.response?.data.status
      ? error.response.data
      : {
          type: "/errors/about:blank",
          title: ErrorCode.UNEXPECTED,
          status,
          detail: error.message || ErrorMessage.UNKNOWN,
          instance: error.request.responseURL || "",
        };

    return new LxcError(lxcError);
  }

  private static logBackstopJsReady(response: AxiosResponse) {
    console.log(response);
    if (
      response.config.url &&
      response.config.baseURL?.includes("http://localhost:")
    ) {
      console.log(
        `backstopjs_ready_${response.config.url.substring(response.config.url.lastIndexOf("/") + 1)}`,
      );
    }
  }
}
